import {
  Clear as ClearIcon,
  Sensors as SensorsIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { AdminEmptyState, AdminFilterPanel, PageShell } from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import PlaceholderImage from "@/components/common/PlaceholderImage/PlaceholderImage";
import { useAuth } from "@/contexts/AuthContext";
import InventoryService, {
  type InventoryItem,
} from "@/services/features/inventory/InventoryService";
import { PusherService } from "@/services/features/PusherService";
import { type Store, StoreService } from "@/services/features/StoreService";
import {
  type CentralRfidActivity as CentralRfidActivityEvent,
  type CentralRfidActivityItem,
} from "@/types/pusher";
import { formatInteger } from "@/utils/number";
import { UserRole } from "@/utils/roleManager";
import { formatStoreSelectorLabel } from "@/utils/store";
import { formatStoreDateTimeWithTimezone } from "@/utils/storeTime";

interface ProductDetails {
  title: string;
  imageUrl: string;
}

const isDemoStore = (store: Store) =>
  store.name.toLowerCase().includes("demo") || store.group.toLowerCase().includes("demo");

interface ItemTableProps {
  items: CentralRfidActivityItem[];
  title: string;
  emptyLabel: string;
  ignoredLabel: string;
  keptLabel: string;
  epcLabel: string;
  productLabel: string;
  skuLabel: string;
  statusLabel: string;
  notAvailableLabel: string;
}

const ItemTable: React.FC<ItemTableProps> = ({
  items,
  title,
  emptyLabel,
  ignoredLabel,
  keptLabel,
  epcLabel,
  productLabel,
  skuLabel,
  statusLabel,
  notAvailableLabel,
}) => (
  <Paper variant="outlined" sx={{ minWidth: 0, overflow: "hidden" }}>
    <Box sx={{ px: 2, py: 1.5, bgcolor: "action.hover" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
    </Box>
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{productLabel}</TableCell>
            <TableCell>{epcLabel}</TableCell>
            <TableCell>{skuLabel}</TableCell>
            <TableCell>{statusLabel}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <AdminEmptyState icon={<SensorsIcon sx={{ fontSize: 40 }} />} text={emptyLabel} />
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => (
              <TableRow key={item.epc} sx={{ opacity: item.ignored ? 0.7 : 1 }}>
                <TableCell>
                  <Stack
                    direction="row"
                    spacing={1.25}
                    sx={{ alignItems: "center", minWidth: 180 }}
                  >
                    <PlaceholderImage
                      alt={item.title || item.sku || notAvailableLabel}
                      className="w-12 h-12 shrink-0 rounded"
                      imageUrl={item.imageUrl}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.title || notAvailableLabel}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}>
                  {item.epc}
                </TableCell>
                <TableCell sx={{ overflowWrap: "anywhere" }}>
                  {item.sku || notAvailableLabel}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={item.ignored ? ignoredLabel : keptLabel}
                    color={item.ignored ? "warning" : "success"}
                    variant={item.ignored ? "filled" : "outlined"}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);

const CentralRfidActivity: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const pusherService = React.useMemo(() => PusherService.getInstance(), []);
  const inventoryService = React.useMemo(
    () => (user?.token ? new InventoryService(user.token) : null),
    [user?.token],
  );
  const storeService = React.useMemo(() => StoreService.getInstance(), []);
  const productCacheRef = React.useRef<Map<string, ProductDetails | null>>(new Map());
  const [stores, setStores] = React.useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = React.useState<Store | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [activities, setActivities] = React.useState<CentralRfidActivityEvent[]>([]);

  React.useEffect(() => {
    let isMounted = true;

    void storeService
      .getStores()
      .then((response) => {
        if (isMounted && response.data) {
          setStores(response.data);
        }
      })
      .catch(() => {
        // Keep the selector empty when stores cannot be loaded.
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [storeService]);

  const visibleStores = React.useMemo(() => {
    return user?.role === UserRole.CRAVE_ADMIN
      ? stores
      : stores.filter((store) => !isDemoStore(store));
  }, [stores, user?.role]);

  const enrichActivity = React.useCallback(
    async (activity: CentralRfidActivityEvent): Promise<CentralRfidActivityEvent> => {
      if (!inventoryService) {
        return activity;
      }

      const epcBySku = new Map<string, string>();
      for (const item of [...activity.originalItems, ...activity.cleanedItems]) {
        if (item.sku && !epcBySku.has(item.sku)) {
          epcBySku.set(item.sku, item.epc);
        }
      }

      for (const [sku, epc] of epcBySku) {
        const cacheKey = `${activity.storeId}:${sku}`;
        if (productCacheRef.current.has(cacheKey)) {
          continue;
        }

        try {
          let inventoryItem: InventoryItem;
          try {
            inventoryItem = await inventoryService.parseEpc(activity.storeId, epc);
          } catch {
            inventoryItem = await inventoryService.getSkuBySkuId(activity.storeId, sku);
          }
          productCacheRef.current.set(cacheKey, {
            title: inventoryItem.title || "",
            imageUrl: inventoryItem.imageUrl || "",
          });
        } catch {
          productCacheRef.current.set(cacheKey, null);
        }
      }

      const enrichItems = (items: CentralRfidActivityItem[]) =>
        items.map((item) => {
          const product = item.sku
            ? productCacheRef.current.get(`${activity.storeId}:${item.sku}`)
            : null;
          return {
            ...item,
            title: product?.title || null,
            imageUrl: product?.imageUrl || null,
          };
        });

      return {
        ...activity,
        originalItems: enrichItems(activity.originalItems),
        cleanedItems: enrichItems(activity.cleanedItems),
      };
    },
    [inventoryService],
  );

  React.useEffect(() => {
    document.title = `${t("centralRfidActivity.title")} - Crave Admin`;
  }, [t]);

  React.useEffect(() => {
    if (!user?.token) {
      return;
    }

    pusherService.initialize(user.token);
    return () => pusherService.disconnect();
  }, [pusherService, user?.token]);

  React.useEffect(() => {
    setActivities([]);
    if (!selectedStore?.id || !user?.token) {
      return;
    }

    pusherService.subscribeCentralRfidActivity(selectedStore.id, (activity) => {
      setActivities((current) => [activity, ...current]);
      void enrichActivity(activity).then((enrichedActivity) => {
        setActivities((current) =>
          current.map((entry) => (entry.id === enrichedActivity.id ? enrichedActivity : entry)),
        );
      });
    });

    return () => pusherService.unsubscribeCentralRfidActivity(selectedStore.id);
  }, [enrichActivity, pusherService, selectedStore?.id, user?.token]);

  const locale = i18n.resolvedLanguage || i18n.language;
  return (
    <PageShell
      breadcrumbs="admin"
      title={t("centralRfidActivity.title")}
      icon={<SensorsIcon />}
      titleAccessory={
        loading || selectedStore ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <LoadingRing loading={loading} />
            {selectedStore && (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                icon={<VisibilityIcon />}
                label={t("centralRfidActivity.status.listening")}
              />
            )}
          </Stack>
        ) : undefined
      }
      headerActions={
        <Button
          variant="outlined"
          startIcon={<ClearIcon />}
          disabled={activities.length === 0}
          onClick={() => setActivities([])}
        >
          {t("centralRfidActivity.actions.clear")}
        </Button>
      }
    >
      <AdminFilterPanel>
        <MuiAutocompleteMultiSelect
          label={t("centralRfidActivity.fields.store")}
          placeholder={t("centralRfidActivity.fields.selectStore")}
          options={visibleStores}
          value={selectedStore ? [selectedStore] : []}
          loading={loading}
          disabled={loading}
          onChange={(next) => setSelectedStore(next[0] ?? null)}
          getOptionLabel={(store) =>
            formatStoreSelectorLabel(store, t, user?.role, "id-em-dash-name")
          }
          getOptionKey={(store) => store.id}
          sx={{ width: "100%" }}
        />
      </AdminFilterPanel>

      {!selectedStore ? (
        <Paper variant="outlined" sx={{ py: 8, px: 2, textAlign: "center" }}>
          <AdminEmptyState
            icon={<SensorsIcon sx={{ fontSize: 44 }} />}
            text={t("centralRfidActivity.description")}
          />
        </Paper>
      ) : activities.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 8, px: 2, textAlign: "center" }}>
          <AdminEmptyState
            icon={<SensorsIcon sx={{ fontSize: 44 }} />}
            text={t("centralRfidActivity.empty.waitingTitle")}
            description={t("centralRfidActivity.empty.waitingDescription", {
              store: selectedStore.name,
            })}
          />
        </Paper>
      ) : (
        <Stack spacing={2}>
          {activities.map((activity) => {
            const ignoredCount = activity.originalItems.filter((item) => item.ignored).length;
            return (
              <Card key={activity.id} variant="outlined" sx={{ overflow: "hidden" }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ p: 2, justifyContent: "space-between", alignItems: { sm: "center" } }}
                >
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip label={activity.direction || t("centralRfidActivity.notAvailable")} />
                    <Chip
                      variant="outlined"
                      label={activity.event || t("centralRfidActivity.notAvailable")}
                    />
                    <Chip
                      color={ignoredCount > 0 ? "warning" : "default"}
                      variant="outlined"
                      label={t("centralRfidActivity.summary.ignored", {
                        count: ignoredCount,
                        formattedCount: formatInteger(ignoredCount, locale),
                      })}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {formatStoreDateTimeWithTimezone(
                      activity.timestamp,
                      selectedStore.timezone,
                      locale,
                    )}
                  </Typography>
                </Stack>
                <Divider />
                <Box
                  sx={{
                    p: 2,
                    display: "grid",
                    gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(2, minmax(0, 1fr))" },
                    gap: 2,
                  }}
                >
                  <ItemTable
                    items={activity.originalItems}
                    title={t("centralRfidActivity.sections.original", {
                      count: activity.originalItems.length,
                      formattedCount: formatInteger(activity.originalItems.length, locale),
                    })}
                    emptyLabel={t("centralRfidActivity.empty.noItems")}
                    ignoredLabel={t("centralRfidActivity.status.ignored")}
                    keptLabel={t("centralRfidActivity.status.kept")}
                    epcLabel={t("centralRfidActivity.fields.epc")}
                    productLabel={t("centralRfidActivity.fields.product")}
                    skuLabel={t("centralRfidActivity.fields.sku")}
                    statusLabel={t("centralRfidActivity.fields.status")}
                    notAvailableLabel={t("centralRfidActivity.notAvailable")}
                  />
                  <ItemTable
                    items={activity.cleanedItems}
                    title={t("centralRfidActivity.sections.cleaned", {
                      count: activity.cleanedItems.length,
                      formattedCount: formatInteger(activity.cleanedItems.length, locale),
                    })}
                    emptyLabel={t("centralRfidActivity.empty.noItems")}
                    ignoredLabel={t("centralRfidActivity.status.ignored")}
                    keptLabel={t("centralRfidActivity.status.kept")}
                    epcLabel={t("centralRfidActivity.fields.epc")}
                    productLabel={t("centralRfidActivity.fields.product")}
                    skuLabel={t("centralRfidActivity.fields.sku")}
                    statusLabel={t("centralRfidActivity.fields.status")}
                    notAvailableLabel={t("centralRfidActivity.notAvailable")}
                  />
                </Box>
              </Card>
            );
          })}
        </Stack>
      )}
    </PageShell>
  );
};

export default CentralRfidActivity;
